import React from 'react';
import { colors } from '@alixpartners/ui-components';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

import {
  ChartContainer,
  LegendItem,
  LegendContainer,
  LegendColor,
  LegendName,
  LegendTitle,
} from './Chart.styles';
import { ICurrencyFormat } from 'utils/types';
import { currencyLabelFormatter, getFormattedPercentage } from 'utils/format';
import { DISPLAY_BY } from 'utils/constants';
import { IChartLegend } from '../types';
import { getChartColor, NON_PRODUCT_COST } from '../utils';
import { singleBartooltipRenderer } from './utils';

type Serie = {
  data: any;
  name?: string;
  color?: any;
  grouping?: boolean;
};

export interface IApplicationChartProps {
  categories: string[];
  displayType: string;
  format: ICurrencyFormat;
  isPercentageAxis: boolean;
  legend: IChartLegend;
  onClick: (c?: string, i?: boolean) => void;
  selectedCategory?: string;
  series: Serie[];
  width?: string;
  chartRef?: any;
}

const getPlotBands = ({
  categories,
  onClick,
  selectedCategory,
}: {
  categories: string[];
  onClick: (c?: string) => void;
  selectedCategory?: string;
}) => {
  const selectedIndex = selectedCategory
    ? categories.findIndex((item: string) => item === selectedCategory)
    : -1;

  return categories.map((category: string, index: number) => ({
    from: index - 0.5,
    to: index + 0.5,
    color: index === selectedIndex ? colors.greyBg : 'transparent',
    events: {
      click: () => {
        const selection =
          index === selectedIndex || category.indexOf('Total') === 0
            ? undefined
            : category;

        onClick(selection);
      },
    },
  }));
};

const transformSelection = (chartRef?: any) => {
  const chart = chartRef && chartRef.current && chartRef.current.chart;
  if (
    chart &&
    chart.xAxis &&
    chart.xAxis[0] &&
    chart.xAxis[0].plotLinesAndBands
  ) {
    chart.xAxis[0].plotLinesAndBands.forEach((plotBand: any) => {
      plotBand.svgElem.attr({
        transform: 'translate(0, -42) scale(1, 2.054)',
      });
    });
  }
};

const getGroupPaddingByDataLength = (dataLength: number) => {
  if (dataLength <= 3) {
    return 0.35;
  }
  if (dataLength <= 9) {
    return 0.25;
  }
  if (dataLength <= 12) {
    return 0.07;
  }
  return 0.03;
};

export const Chart = ({
  categories,
  chartRef,
  displayType,
  format,
  isPercentageAxis,
  legend,
  onClick,
  selectedCategory,
  series: seriesRaw,
  width = '100%',
}: IApplicationChartProps) => {
  const isNumber = displayType === DISPLAY_BY.HEADCOUNT;
  const tooltipBarId = 'tooltip-bar-123-' + Math.ceil(Math.random() * 1000);
  const series = seriesRaw.map((serie) => ({
    ...serie,
    marker: {
      symbol: 'circle',
    },
  }));
  let yAxis: any = {
    min: 0,
    height: '100%',
    title: {
      enabled: false,
    },
    labels: {
      // @ts-ignore
      formatter: function () {
        if (isNumber) {
          // @ts-ignore
          return this.value;
        }
        // @ts-ignore
        return currencyLabelFormatter(this.value, format);
      },
    },
  };
  if (isPercentageAxis) {
    yAxis = [
      yAxis,
      {
        min: 0,
        height: '100%',
        title: {
          enabled: false,
        },
        labels: {
          // @ts-ignore
          formatter: function () {
            return `<strong>${getFormattedPercentage(
              // @ts-ignore
              this.value,
              format
            )}</strong>`;
          },
        },
        opposite: true,
      },
    ];
  }
  const chartOptions = {
    chart: {
      type: 'column',
      spacingTop: 40,
      className: 'line-chart',
      style: { fontFamily: 'Roboto' },
      marginTop: 40,
      spacingBottom: 40,
      height: 500,
      animation: false,
      events: {
        redraw: () => {
          transformSelection(chartRef);
        },
        render: function (e: any) {
          const chart = e.target;
          if (
            chart.series.length &&
            chart.series[0] &&
            chart.series[0].points &&
            chart.series[0].points[0]
          ) {
            const { pointWidth } = chart.series[0].points[0];
            const nonProductSerie = chart.series.find(
              (serie: any) => serie.name === NON_PRODUCT_COST
            );
            if (nonProductSerie) {
              const { pointWidth: lastColumnWidth } = nonProductSerie.points[0];
              if (
                pointWidth &&
                lastColumnWidth &&
                Math.floor(pointWidth) !== Math.floor(lastColumnWidth)
              ) {
                nonProductSerie.update({ pointWidth });
              }
            }
          }
        },
      },
    },
    loading: {
      hideDuration: 500,
      showDuration: 500,
      labelStyle: {
        fontWeight: 'normal',
        letterSpacing: '0.3px',
      },
    },
    title: {
      text: null,
    },
    xAxis: {
      categories,
      labels: {
        autoRotation: undefined,
        style: {
          color: colors.grey7,
          fontSize: '11px',
          letterSpacing: '0.3px',
          fontWeight: 500,
        },
      },
      plotBands: getPlotBands({
        categories,
        onClick,
        selectedCategory,
      }),
    },
    legend: {
      enabled: false,
    },
    yAxis,
    credits: { enabled: false },
    exporting: { enabled: false },
    tooltip: {
      useHTML: true,
      outside: true,
      backgroundColor: colors.white,
      borderWidth: 0,
      borderRadius: 2,
      // @ts-ignore
      formatter: function () {
        // @ts-ignore
        const { point, x } = this;
        return singleBartooltipRenderer({
          tooltipBarId,
          displayType,
          format,
          x,
          series,
          highlightedSerie: point.title,
        });
      },
    },
    plotOptions: {
      line: {
        states: {
          hover: {
            symbol: 'circle',
          },
        },
      },
      column: {
        pointPadding: 0,
        groupPadding: getGroupPaddingByDataLength(
          (series && series[0]?.data?.length) || 10
        ),
      },
      series: {
        cursor: 'pointer',
        events: {
          // @ts-ignore
          click: function (e) {
            const isSelectingCategory = e.point.isClickDisabled;
            if (e.point.category !== NON_PRODUCT_COST) {
              onClick(e.point.category, isSelectingCategory);
            }
          },
        },
        marker: {
          enabled: false,
          symbol: 'circle',
          lineColor: null,
        },
        states: {
          hover: {
            marker: {
              enabled: true,
              symbol: 'circle',
              fillColor: colors.white,
              lineColor: null,
            },
          },
        },
        dataLabels: {
          enabled: true,
          verticalAlign: 'top',
          y: -24,
          // @ts-ignore
          formatter: function (e) {
            if (isNumber) {
              return this.y;
            }
            // @ts-ignore
            if (this.point.isPercentage) {
              // @ts-ignore
              return '';
            }
            // @ts-ignore
            return currencyLabelFormatter(this.y, format);
          },
          style: {
            color: 'black',
            textOutline: '0px contrast',
            'stroke-width': 0,
          },
        },
      },
    },
    series,
  };

  return (
    <ChartContainer width={width}>
      <LegendContainer>
        <LegendTitle>{legend.title}:</LegendTitle>
        {legend.items.map((item) => (
          <LegendItem key={`${item}-${getChartColor(item)}`}>
            <LegendColor color={getChartColor(item)} />
            <LegendName>{item}</LegendName>
          </LegendItem>
        ))}
      </LegendContainer>
      <HighchartsReact
        highcharts={Highcharts}
        options={chartOptions}
        ref={chartRef}
      />
    </ChartContainer>
  );
};

export default Chart;
